<template>
  <div v-if="user" class="kartei-container py-6 px-4">
    <div class="kartei-wrap">
    <!-- Back Bar -->
    <div class="d-flex align-center mb-4">
      <v-btn icon dark @click="goBack" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="grey--text text-caption">Zurück zum Teacher-Dashboard</span>
    </div>

    <!-- Header -->
    <v-card class="header-card rounded-xl pa-4 pa-md-6 mb-6 d-flex flex-wrap align-center justify-space-between gap-3" dark elevation="0">
      <div class="d-flex align-center">
        <v-avatar size="80" color="grey darken-3" class="mr-6">
          <span class="text-h4 font-weight-bold gold-text">{{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}</span>
        </v-avatar>
        <div>
          <h2 class="text-h4 font-weight-bold white--text">{{ user.firstName }} {{ user.lastName }}</h2>
          <div class="d-flex align-center mt-1 grey--text">
            <v-icon small class="mr-1">mdi-domain</v-icon>
            <span>{{ user.studio }}</span>
            <span class="mx-3">•</span>
            <v-icon small class="mr-1">mdi-account-badge-outline</v-icon>
            <span>{{ user.role || "Member" }}</span>
          </div>
        </div>
      </div>

      <v-btn outlined color="#D4AF37" @click="exportPdf">
        <v-icon left>mdi-printer</v-icon>
        Drucken / PDF
      </v-btn>
    </v-card>

    <v-row>
      <!-- Lizenzen -->
      <v-col cols="12" md="6">
        <v-card class="section-card rounded-xl pa-6 h-100" dark elevation="0">
          <h3 class="text-h6 font-weight-bold mb-4 d-flex align-center">
            <v-icon color="#D4AF37" class="mr-2">mdi-medal-outline</v-icon>
            Aktive Lizenzen
          </h3>
          <div v-if="licenses.length > 0">
            <div v-for="lic in licenses" :key="lic._id" class="d-flex justify-space-between align-center mb-3 pa-3 bg-dark rounded-lg border-gold">
              <span class="text-capitalize font-weight-bold">{{ lic.tier }}</span>
              <div class="text-right">
                <div class="text-caption grey--text">Ablaufdatum</div>
                <div :class="{ 'red--text': lic.status === 'expiring_soon' || lic.status === 'expired' }">{{ formatDate(lic.expiresAt) }}</div>
              </div>
            </div>
          </div>
          <div v-else class="grey--text text-caption">Keine Lizenzen vorhanden.</div>
        </v-card>
      </v-col>

      <!-- Modul Fortschritt -->
      <v-col cols="12" md="6">
        <v-card class="section-card rounded-xl pa-6 h-100" dark elevation="0">
          <h3 class="text-h6 font-weight-bold mb-4 d-flex align-center">
            <v-icon color="#D4AF37" class="mr-2">mdi-chart-donut</v-icon>
            Modul-Fortschritt
          </h3>
          <div v-for="prog in progressData" :key="prog.moduleId" class="mb-4">
            <div class="d-flex justify-space-between text-caption mb-1">
              <span>{{ prog.moduleTitle }}</span>
              <span class="gold-text">{{ prog.percent }}%</span>
            </div>
            <v-progress-linear :value="prog.percent" color="#D4AF37" background-color="rgba(255,255,255,0.1)" rounded></v-progress-linear>
          </div>
        </v-card>
      </v-col>

      <!-- Schulungen & Quiz -->
      <v-col cols="12" md="6">
        <v-card class="section-card rounded-xl pa-6 h-100" dark elevation="0">
          <h3 class="text-h6 font-weight-bold mb-4 d-flex align-center">
            <v-icon color="#D4AF37" class="mr-2">mdi-calendar-check</v-icon>
            Schulungen & Tests
          </h3>
          <div class="mb-4">
            <div class="font-weight-bold mb-2 text-caption grey--text text-uppercase">Schulungen</div>
            <div v-for="reg in registrations" :key="reg._id" class="text-body-2 mb-1 d-flex justify-space-between">
              <span>{{ reg.terminTitle }}</span>
              <span :class="{ 'green--text': reg.status === 'attended', 'grey--text': reg.status === 'registered' }">{{ reg.status }}</span>
            </div>
          </div>
          <div>
            <div class="font-weight-bold mb-2 text-caption grey--text text-uppercase">Letzte Quizzes</div>
            <div v-for="q in quizHistory" :key="q.date" class="text-body-2 mb-1 d-flex justify-space-between">
              <span>{{ q.moduleTitle }}</span>
              <span :class="q.passed ? 'green--text' : 'red--text'">{{ q.score }}%</span>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- Notizen -->
      <v-col cols="12" md="6">
        <v-card class="section-card rounded-xl pa-6 h-100" dark elevation="0">
          <h3 class="text-h6 font-weight-bold mb-4 d-flex align-center">
            <v-icon color="#D4AF37" class="mr-2">mdi-note-edit-outline</v-icon>
            Teacher-Notizen
          </h3>

          <v-textarea v-model="newNote" outlined color="#D4AF37" label="Neue Notiz hinzufügen..." rows="2" hide-details class="mb-3"></v-textarea>
          <div class="d-flex justify-end mb-4">
            <v-btn color="#D4AF37" class="black--text" small rounded @click="saveNote" :loading="savingNote">Speichern</v-btn>
          </div>

          <v-divider class="mb-4 border-white-05"></v-divider>

          <div class="notes-list" style="max-height: 200px; overflow-y: auto">
            <div v-for="note in notes" :key="note._id" class="mb-3 pa-3 bg-dark rounded-lg">
              <div class="d-flex justify-space-between text-caption grey--text mb-1">
                <span>{{ note.authorName }}</span>
                <span>{{ formatDate(note.createdAt) }}</span>
              </div>
              <p class="mb-0 text-body-2">{{ note.text }}</p>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
    </div>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "UserKartei",
  data() {
    return {
      user: null,
      licenses: [],
      progressData: [],
      registrations: [],
      quizHistory: [],
      notes: [],
      newNote: "",
      savingNote: false,
      loading: false,
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await api.akademie.fetchUserKartei(this.$route.params.id)
        if (res) {
          this.user = res.user
          this.notes = res.notes || []
          this.licenses = res.licenses || []
          this.progressData = res.progressData || []
          this.registrations = res.registrations || []
          this.quizHistory = res.quizHistory || []
        }
      } catch (err) {
        console.error("Fehler beim Laden der Kartei:", err)
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH")
    },
    async saveNote() {
      if (!this.newNote) return
      this.savingNote = true
      try {
        await api.akademie.addTeacherNote(this.user._id, this.newNote)
        this.newNote = ""
        // Reload data or optimistic insert
        this.loadData()
      } catch (err) {
        console.error("Fehler beim Speichern der Notiz:", err)
      } finally {
        this.savingNote = false
      }
    },
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push("/akademie-teacher")
    },
    exportPdf() {
      window.print() // Simple fallback for now
    },
  },
}
</script>

<style scoped>
.kartei-container {
  min-height: 100vh;
  background-color: #121212;
}
.kartei-wrap {
  max-width: 1280px;
  margin: 0 auto;
}

.header-card,
.section-card {
  background: rgba(30, 30, 30, 0.8) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.gold-text {
  color: #d4af37 !important;
}
.border-gold {
  border-left: 3px solid #d4af37;
}

.bg-dark {
  background: rgba(18, 18, 18, 0.6);
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.h-100 {
  height: 100%;
}
.gap-3 { gap: 12px; }

@media print {
  .kartei-container {
    background: white;
  }
  .v-card {
    background: white !important;
    border: 1px solid #ccc !important;
    box-shadow: none !important;
  }
  .white--text {
    color: black !important;
  }
  .gold-text {
    color: #000 !important;
  }
}
</style>
